import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/lib/i18n";
import { SECTIONS, WINES } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WinesSection() {
  const { t, lang } = useI18n();
  const [wines, setWines] = useState([]);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/wines`).then((r) => {
      if (active) setWines(r.data || []);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="wines"
      data-testid={SECTIONS.wines}
      className="relative bg-[#fbf8f3] text-[#1a1a1a] py-28 lg:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#7a1a2d]" />
              <span className="text-[11px] tracking-[0.42em] text-[#7a1a2d] uppercase">
                {t.winesSection.eyebrow}
              </span>
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight">
              {t.winesSection.title}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-[#3a3a3a] leading-relaxed font-light">{t.winesSection.body}</p>
          </div>
        </div>

        <div data-testid={WINES.grid} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#dcd5c5]">
          {wines.map((w, i) => (
            <article
              key={w.id}
              data-testid={WINES.card(w.slug)}
              className={`group relative bg-[#fbf8f3] p-8 lg:p-10 transition-colors duration-500 hover:bg-white ${
                w.featured ? "lg:row-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.32em] text-[#7a1a2d]">
                  N°{String(i + 1).padStart(2, "0")}
                </span>
                {w.featured && (
                  <span className="text-[10px] tracking-[0.32em] uppercase bg-[#1a1a1a] text-[#c5a572] px-3 py-1">
                    Signature
                  </span>
                )}
              </div>
              <h3 className="mt-8 font-serif text-3xl lg:text-4xl leading-tight">{w.name}</h3>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#5a5a5a]">
                  {w.type}
                </div>
                {w.vintage && (
                  <div className="font-mono text-[11px] tracking-[0.2em] text-[#7a1a2d]">
                    {w.vintage}
                  </div>
                )}
              </div>
              <p className="mt-6 text-[#3a3a3a] leading-relaxed text-sm font-light">
                {lang === "sr" ? w.description_sr : w.description_en}
              </p>
              <div className="mt-6 pt-6 border-t border-[#1a1a1a]/10">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#5a5a5a] mb-2">
                  {lang === "sr" ? "Note" : "Notes"}
                </div>
                <div className="font-serif italic text-[#1a1a1a]/80 text-sm">
                  {lang === "sr" ? w.notes_sr : w.notes_en}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
